import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { colors, radius, shadow, spacing, typography } from '../theme/tokens';
import { joinRange } from '../utils/cvDates';
import { DEFAULT_ACCENT, readableOn } from '../theme/templates';
import type { CvTemplate, GeneratedCv, OutputLanguage } from '../types';
import { cvSectionLabel, type CvSectionKey } from '../utils/documentLabels';
import { PROPS_LTR } from '../i18n/sens';
import { SensTexteLtr } from './Texte';

const BLACK = colors.grey900;

function Section({ label, color = BLACK, children }: { label: string; color?: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color }]}>{label.toUpperCase()}</Text>
      <View style={[styles.sectionRule, { backgroundColor: color }]} />
      {children}
    </View>
  );
}

// Entrée datée : date à gauche (~22%), contenu à droite (titre gras + meta + bullets).
function DatedEntry({
  date,
  title,
  meta,
  bullets,
  testID,
}: {
  date: string;
  title: string;
  meta?: string;
  bullets?: string[];
  testID?: string;
}) {
  return (
    <View testID={testID} style={styles.entry}>
      <Text style={styles.entryDate}>{date}</Text>
      <View style={styles.entryBody}>
        <Text style={styles.itemTitle}>{title}</Text>
        {meta ? <Text style={styles.itemMeta}>{meta}</Text> : null}
        {bullets?.map((bullet, i) => (
          <View key={i} style={styles.bulletRow}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>{bullet}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

interface CvPreviewProps {
  cv: GeneratedCv;
  name?: string;
  title?: string; // repli si cv.headline est vide
  address?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  template?: CvTemplate;
  accentColor?: string;
  // Langue du DOCUMENT, pas de l'interface : les intitulés de sections la suivent, exactement
  // comme à l'export PDF. L'aperçu doit montrer ce qui sera imprimé.
  outputLanguage?: OutputLanguage;
  testID?: string;
}

// Aperçu lecture seule — reflète exactement le PDF (template Mono N&B ou Coloré à bande latérale).
export function CvPreview({
  cv,
  name,
  title,
  address,
  email,
  phone,
  linkedin,
  template = 'mono',
  accentColor = DEFAULT_ACCENT,
  outputLanguage = 'fr',
  testID,
}: CvPreviewProps) {
  const label = (key: CvSectionKey) => cvSectionLabel(key, outputLanguage);
  const headline = (cv.headline?.trim() || title?.trim()) ?? '';
  const contactLines = (
    [
      ['Email', email],
      ['Tel', phone],
      ['LinkedIn', linkedin],
      ['Adresse', address],
    ] as const
  ).filter(([, value]) => value?.trim());

  // Sections partagées entre les deux templates (la couleur des titres varie).
  const experiences = (color: string) =>
    cv.experiences.length > 0 ? (
      <Section label={label('experiences')} color={color}>
        {cv.experiences.map((exp, index) => (
          <DatedEntry
            key={`${exp.role}-${index}`}
            testID={`cv-exp-${index}`}
            date={joinRange(exp.start_date, exp.end_date)}
            title={exp.role}
            meta={exp.company}
            bullets={exp.bullets}
          />
        ))}
      </Section>
    ) : null;

  const projects = (color: string) =>
    cv.projects.length > 0 ? (
      <Section label={label('projects')} color={color}>
        {cv.projects.map((proj, index) => (
          <DatedEntry
            key={`${proj.name}-${index}`}
            testID={`cv-proj-${index}`}
            date={proj.date}
            title={proj.name}
            bullets={proj.bullets}
          />
        ))}
      </Section>
    ) : null;

  const education = (color: string) =>
    cv.education.length > 0 ? (
      <Section label={label('education')} color={color}>
        {cv.education.map((edu, index) => (
          <DatedEntry
            key={`${edu.degree}-${index}`}
            testID={`cv-edu-${index}`}
            date={edu.date}
            title={edu.degree}
            meta={edu.school}
          />
        ))}
      </Section>
    ) : null;

  const interests = (color: string) =>
    cv.interests.length > 0 ? (
      <Section label={label('interests')} color={color}>
        <Text style={styles.inline}>{cv.interests.join(' · ')}</Text>
      </Section>
    ) : null;

  // ───────────── Template « Coloré » : bande latérale gauche + contenu à droite ─────────────
  if (template === 'accent') {
    const onAccent = readableOn(accentColor);
    return (
      <View testID={testID} style={styles.page} {...PROPS_LTR}>
        <SensTexteLtr>
          <View style={styles.accentRow}>
            <View testID="cv-sidebar" style={[styles.sidebar, { backgroundColor: accentColor }]}>
              {name?.trim() ? (
                <Text testID="cv-name" style={[styles.sidebarName, { color: onAccent }]}>
                  {name.trim()}
                </Text>
              ) : null}
              {contactLines.length > 0 ? (
                <View testID="cv-contact" style={styles.sidebarBlock}>
                  {contactLines.map(([label, value]) => (
                    <Text key={label} style={[styles.sidebarContact, { color: onAccent }]}>
                      {label} : {value!.trim()}
                    </Text>
                  ))}
                </View>
              ) : null}
              {cv.skills.length > 0 ? (
                <View style={styles.sidebarBlock}>
                  <Text style={[styles.sidebarHeading, { color: onAccent }]}>{label('skills').toUpperCase()}</Text>
                  {cv.skills.map((s) => (
                    <Text key={s} style={[styles.sidebarItem, { color: onAccent }]}>{s}</Text>
                  ))}
                </View>
              ) : null}
              {cv.languages.length > 0 ? (
                <View style={styles.sidebarBlock}>
                  <Text style={[styles.sidebarHeading, { color: onAccent }]}>{label('languages').toUpperCase()}</Text>
                  {cv.languages.map((l) => (
                    <Text key={l} style={[styles.sidebarItem, { color: onAccent }]}>{l}</Text>
                  ))}
                </View>
              ) : null}
            </View>

            <View testID="cv-main" style={styles.main}>
              {headline ? (
                <Text testID="cv-headline" style={[styles.accentHeadline, { color: accentColor }]}>
                  {headline}
                </Text>
              ) : null}
              {experiences(accentColor)}
              {projects(accentColor)}
              {education(accentColor)}
              {interests(accentColor)}
            </View>
          </View>
        </SensTexteLtr>
      </View>
    );
  }

  // ───────────── Template « Mono » (défaut) : N&B, en-tête à gauche + titre centré ─────────────
  return (
    <View testID={testID} style={styles.page} {...PROPS_LTR}>
      <SensTexteLtr>
        <View style={styles.header}>
          <View style={styles.headLeft}>
            {name?.trim() ? (
              <Text testID="cv-name" style={styles.name}>
                {name.trim()}
              </Text>
            ) : null}
            {contactLines.length > 0 ? (
              <View testID="cv-contact" style={styles.contactBlock}>
                {contactLines.map(([label, value]) => (
                  <Text key={label} style={styles.contact}>
                    {label} : {value!.trim()}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
          {headline ? (
            <Text testID="cv-headline" style={styles.headline}>
              {headline}
            </Text>
          ) : null}
        </View>

        {experiences(BLACK)}
        {projects(BLACK)}
        {education(BLACK)}

        {cv.skills.length > 0 ? (
          <Section label={label('skills')}>
            <Text style={styles.inline}>{cv.skills.join(' · ')}</Text>
          </Section>
        ) : null}
        {cv.languages.length > 0 ? (
          <Section label={label('languages')}>
            <Text style={styles.inline}>{cv.languages.join(' · ')}</Text>
          </Section>
        ) : null}
        {interests(BLACK)}
      </SensTexteLtr>
    </View>
  );
}

const styles = StyleSheet.create({
  // Document en français : jamais retourné en droite à gauche (i18n/sens.ts).
  page: {
    direction: 'ltr',
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    padding: spacing.xl,
    gap: spacing.md,
    ...shadow.card,
  },
  header: { gap: spacing.sm, marginBottom: spacing.xs },
  headLeft: { alignItems: 'flex-start' },
  name: { fontSize: typography.small.size, fontWeight: '700', color: BLACK, marginBottom: spacing.xs / 2 },
  contactBlock: { alignItems: 'flex-start' },
  contact: { fontSize: typography.small.size, lineHeight: typography.small.size + 3, color: colors.grey700 },
  headline: {
    fontSize: typography.h3.size,
    fontWeight: typography.h3.weight,
    color: BLACK,
    textAlign: 'center',
  },
  section: { gap: spacing.xs },
  sectionLabel: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    letterSpacing: typography.caption.letterSpacing,
    color: BLACK,
  },
  sectionRule: { height: 2, backgroundColor: BLACK },
  entry: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  entryDate: { width: '22%', fontSize: typography.small.size, color: colors.grey700 },
  entryBody: { flex: 1 },
  itemTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: BLACK },
  itemMeta: { marginTop: spacing.xs / 2, fontSize: typography.small.size, color: colors.grey700 },
  bulletRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs / 2 },
  bulletDot: { color: BLACK, fontSize: typography.body.size },
  bulletText: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: BLACK },
  inline: { marginTop: spacing.xs, fontSize: typography.body.size, color: BLACK },
  // ── Template accent ──
  accentRow: { flexDirection: 'row', gap: spacing.md },
  sidebar: { width: '34%', borderRadius: radius.md, padding: spacing.md, gap: spacing.md },
  sidebarBlock: { gap: spacing.xs / 2 },
  sidebarName: { fontSize: typography.bodyMd.size, fontWeight: '700' },
  sidebarContact: { fontSize: typography.small.size, lineHeight: typography.small.size + 3 },
  sidebarHeading: { fontSize: typography.caption.size, fontWeight: '700', letterSpacing: typography.caption.letterSpacing },
  sidebarItem: { fontSize: typography.small.size, lineHeight: typography.small.size + 3 },
  main: { flex: 1, gap: spacing.md },
  accentHeadline: { fontSize: typography.h3.size, fontWeight: typography.h3.weight },
});
